import React, { useRef, useState } from "react";
import { Upload, X, GripVertical, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";

/**
 * Property image uploader.
 * - Up to `max` (default 15) images
 * - Drag-to-reorder
 * - Upload via multipart POST /api/uploads/image (stores in object storage)
 * - Value shape: string[] of image URLs (either uploaded /api/files/... or external https)
 */
export default function ImageUploader({ value = [], onChange, max = 15, testId = "image-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const inputRef = useRef(null);

  async function handleFiles(files) {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    const room = max - value.length;
    if (room <= 0) {
      toast.error(`Max ${max} images. Remove one first.`);
      return;
    }
    const picked = list.slice(0, room);
    if (list.length > room) toast.info(`Only added ${room} — max ${max} images.`);
    setUploading(true);
    const uploaded = [];
    for (const file of picked) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (e) {
        toast.error(`Could not upload ${file.name}`);
      }
    }
    if (uploaded.length) onChange([...value, ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  function remove(idx) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function reorder(from, to) {
    if (from === to || from == null || to == null) return;
    const copy = value.slice();
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    onChange(copy);
  }

  return (
    <div data-testid={testId}>
      <label
        htmlFor={`${testId}-input`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
        }}
        className="block cursor-pointer rounded-2xl border-2 border-dashed border-neutral-300 hover:border-propera transition p-6 text-center"
      >
        <div className="w-12 h-12 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-2">
          <ImagePlus size={20} />
        </div>
        <p className="text-sm font-semibold text-neutral-900">
          {uploading ? "Uploading…" : "Click to upload or drag & drop"}
        </p>
        <p className="text-xs text-neutral-500 mt-1">JPEG, PNG or WebP · up to 8&nbsp;MB each · max {max} images</p>
        <input
          ref={inputRef}
          id={`${testId}-input`}
          data-testid={`${testId}-file-input`}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </label>

      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
          {value.map((src, idx) => (
            <div
              key={`${src}-${idx}`}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { reorder(dragIdx, idx); setDragIdx(null); }}
              onDragEnd={() => setDragIdx(null)}
              data-testid={`${testId}-item-${idx}`}
              className={`relative aspect-square rounded-2xl overflow-hidden bg-neutral-100 group cursor-move ${dragIdx === idx ? "opacity-40" : ""}`}
            >
              <SecureImage src={src} alt={`Property image ${idx + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
              <div className="absolute top-2 right-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`${testId}-remove-${idx}`}
                  className="w-7 h-7 rounded-full bg-white/95 text-neutral-700 hover:bg-white flex items-center justify-center shadow"
                  aria-label="Remove image"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="absolute bottom-2 left-2 flex items-center gap-1">
                {idx === 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-propera text-white">Cover</span>
                )}
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-black/60 text-white inline-flex items-center gap-1">
                  <GripVertical size={10} /> {idx + 1}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {value.length > 1 && (
        <p className="text-[11px] text-neutral-500 mt-2">Drag any tile to reorder. The first image is used as the cover.</p>
      )}
    </div>
  );
}

// Loads an image via fetch (adds Authorization header) — needed for /api/files/* which requires ?auth=
// External URLs (http/https) go through the browser directly.
export function SecureImage({ src, className = "", alt = "" }) {
  if (!src) return <div className={`bg-neutral-100 ${className}`} />;
  // If it's one of our authenticated endpoints, append token as query param
  const isProtected = src.startsWith("/api/files/");
  const t = getToken();
  const final = isProtected ? `${src}?auth=${encodeURIComponent(t || "")}` : src;
  return <img src={final} alt={alt} className={className} loading="lazy" />;
}
